import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);
    this.#scheduleVerticalStackAlignment();

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });


  /** @type {MediaQueryList} */
  #verticalLayoutMedia = window.matchMedia('(min-width: 750px)');

  /** @type {number | null} */
  #verticalAlignmentRaf = null;

  /** @type {number} */
  #verticalCollapseThreshold = 24;

  #handleVerticalLayoutChange = () => {
    this.#updateVerticalMenuCollapseState();
    this.#scheduleVerticalStackAlignment();
  };

  #isAdaptiveVerticalDesktop = () =>
    this.dataset.desktopMenuLayout === 'vertical_stack' &&
    this.hasAttribute('data-collapse-vertical-on-scroll') &&
    this.#verticalLayoutMedia.matches;

  #isVerticalMenuTrigger = (target) =>
    target instanceof Element &&
    target.closest(
      ".header__drawer .header__icon--menu, [data-testid='cart-drawer-trigger'], .action__cart"
    );

  #setVerticalMenuOpen = (open) => {
    if (!this.#isAdaptiveVerticalDesktop() || this.dataset.verticalMenuState !== 'collapsed') {
      this.removeAttribute('data-vertical-menu-open');
      return;
    }

    this.toggleAttribute('data-vertical-menu-open', open);

    const menuTrigger = this.querySelector('.header__drawer .header__icon--menu');
    if (menuTrigger instanceof HTMLElement) {
      menuTrigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
  };

  #handleVerticalPointerOver = (event) => {
    if (this.dataset.verticalMenuState !== 'collapsed') return;
    if (this.#isVerticalMenuTrigger(event.target)) this.#setVerticalMenuOpen(true);
  };

  #handleVerticalPointerLeave = () => {
    if (!this.matches(':focus-within')) this.#setVerticalMenuOpen(false);
  };

  #handleVerticalFocusIn = (event) => {
    if (this.dataset.verticalMenuState !== 'collapsed') return;
    if (this.#isVerticalMenuTrigger(event.target)) this.#setVerticalMenuOpen(true);
  };

  #handleVerticalFocusOut = () => {
    queueMicrotask(() => {
      if (!this.contains(document.activeElement) && !this.matches(':hover')) {
        this.#setVerticalMenuOpen(false);
      }
    });
  };

  #handleVerticalClick = (event) => {
    if (!this.#isAdaptiveVerticalDesktop() || this.dataset.verticalMenuState !== 'collapsed') return;
    if (!(event.target instanceof Element)) return;

    const menuTrigger = event.target.closest('.header__drawer .header__icon--menu');
    if (!menuTrigger) return;

    // Desktop uses the compact MENU label as a flyout trigger rather than
    // opening the mobile drawer. The mobile drawer remains unchanged.
    event.preventDefault();
    event.stopImmediatePropagation();
    this.#setVerticalMenuOpen(true);

    // Mouse clicks should not leave a persistent focus lock after the pointer
    // leaves the header. Keyboard activation keeps focus for tab navigation.
    if (event.detail > 0 && menuTrigger instanceof HTMLElement) menuTrigger.blur();
  };

  #handleVerticalKeydown = (event) => {
    if (event.key !== 'Escape' || !this.hasAttribute('data-vertical-menu-open')) return;

    this.#setVerticalMenuOpen(false);
    const menuTrigger = this.querySelector('.header__drawer .header__icon--menu');
    if (menuTrigger instanceof HTMLElement) menuTrigger.focus();
  };

  #updateVerticalMenuCollapseState = () => {
    if (!this.#isAdaptiveVerticalDesktop()) {
      delete this.dataset.verticalMenuState;
      this.removeAttribute('data-vertical-menu-open');
      return;
    }

    const scrollTop = document.scrollingElement?.scrollTop ?? window.scrollY ?? 0;
    const nextState = scrollTop > this.#verticalCollapseThreshold ? 'collapsed' : 'expanded';

    if (this.dataset.verticalMenuState === nextState) return;

    this.dataset.verticalMenuState = nextState;
    if (nextState === 'expanded') this.#setVerticalMenuOpen(false);
    this.#scheduleVerticalStackAlignment();
  };

  #scheduleVerticalStackAlignment = () => {
    if (this.#verticalAlignmentRaf !== null) {
      cancelAnimationFrame(this.#verticalAlignmentRaf);
    }

    this.#verticalAlignmentRaf = requestAnimationFrame(() => {
      this.#verticalAlignmentRaf = null;
      this.#alignVerticalStackToLogo();
      this.#measureVerticalFlyout();
    });
  };

  #alignVerticalStackToLogo = () => {
    this.removeAttribute('data-vertical-stack-aligned');
    this.style.removeProperty('--vertical-content-top-offset');

    if (this.dataset.desktopMenuLayout !== 'vertical_stack' || !this.#verticalLayoutMedia.matches) return;

    const columns = this.querySelector('.header__row--top .header__columns');
    const logoImages = [...this.querySelectorAll('.header__row--top .header-logo__image')];
    const visibleLogo = logoImages.find((image) => image.getClientRects().length > 0);

    if (!(columns instanceof HTMLElement) || !(visibleLogo instanceof HTMLElement)) return;

    const columnsRect = columns.getBoundingClientRect();
    const logoRect = visibleLogo.getBoundingClientRect();
    const topOffset = Math.max(0, Math.round(logoRect.top - columnsRect.top));

    this.style.setProperty('--vertical-content-top-offset', `${topOffset}px`);
    this.setAttribute('data-vertical-stack-aligned', '');
  };

  #measureVerticalFlyout = () => {
    this.style.removeProperty('--vertical-flyout-content-height');

    if (this.dataset.desktopMenuLayout !== 'vertical_stack' || !this.#verticalLayoutMedia.matches) return;

    const menu = this.querySelector('.header__column--left > .header-menu');
    const utilityLinks = [
      ...this.querySelectorAll(
        '.header-actions--vertical-stack .header-actions__account-link, .header-actions--vertical-stack .header-actions__contact'
      ),
    ];

    const menuHeight = menu instanceof HTMLElement ? Math.ceil(menu.getBoundingClientRect().height) : 0;
    const computed = getComputedStyle(this);
    const gap = Number.parseFloat(computed.getPropertyValue('--vertical-menu-gap')) || 0;
    const utilityHeight = utilityLinks.reduce((height, link, index) => {
      if (!(link instanceof HTMLElement)) return height;
      return height + Math.ceil(link.getBoundingClientRect().height) + (index > 0 ? gap : 0);
    }, 0);

    const flyoutHeight = Math.max(menuHeight, utilityHeight);
    if (flyoutHeight > 0) {
      this.style.setProperty('--vertical-flyout-content-height', `${flyoutHeight}px`);
    }
  };

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }
    setHeaderMenuStyle();
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateVerticalMenuCollapseState();
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';

      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);
    this.#verticalLayoutMedia.addEventListener('change', this.#handleVerticalLayoutChange);
    this.addEventListener('pointerover', this.#handleVerticalPointerOver);
    this.addEventListener('pointerleave', this.#handleVerticalPointerLeave);
    this.addEventListener('focusin', this.#handleVerticalFocusIn);
    this.addEventListener('focusout', this.#handleVerticalFocusOut);
    this.addEventListener('click', this.#handleVerticalClick, true);
    this.addEventListener('keydown', this.#handleVerticalKeydown);
    this.#updateVerticalMenuCollapseState();
    this.#scheduleVerticalStackAlignment();

    document.fonts?.ready.then(this.#scheduleVerticalStackAlignment);
    for (const image of this.querySelectorAll('.header-logo__image')) {
      if (image instanceof HTMLImageElement && !image.complete) {
        image.addEventListener('load', this.#scheduleVerticalStackAlignment, { once: true });
      }
    }

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) this.#observeStickyPosition(stickyMode === 'always');

    if (
      stickyMode === 'scroll-up' ||
      stickyMode === 'always' ||
      this.hasAttribute('data-collapse-vertical-on-scroll')
    ) {
      document.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    this.#verticalLayoutMedia.removeEventListener('change', this.#handleVerticalLayoutChange);
    this.removeEventListener('pointerover', this.#handleVerticalPointerOver);
    this.removeEventListener('pointerleave', this.#handleVerticalPointerLeave);
    this.removeEventListener('focusin', this.#handleVerticalFocusIn);
    this.removeEventListener('focusout', this.#handleVerticalFocusOut);
    this.removeEventListener('click', this.#handleVerticalClick, true);
    this.removeEventListener('keydown', this.#handleVerticalKeydown);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    if (this.#verticalAlignmentRaf !== null) {
      cancelAnimationFrame(this.#verticalAlignmentRaf);
      this.#verticalAlignmentRaf = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});